// Book a Call: a video sales letter that ends in booking a call. Modeled on
// Goh Consulting's training funnel (board #21: black canvas, warm yellow,
// "Step 1: watch the video" then "Step 2: book a call", case-study wall),
// with Get More Design Work (#16) for the cinematic video frame.
import type { PageStarter } from './types';
import { AVATAR, footer, img } from './assets';

export const vslCall: PageStarter = {
  key: 'vsl-call',
  name: 'Book a call',
  description: 'Video sales letter: watch the video, then book a strategy call. Case studies and a short FAQ.',
  category: 'calls',
  theme: { style: 'program', font: 'poppins', brand_color: '#F5D547', archetype: 'vsl-call' },
  form: [{ key: 'full_name', step: 1 }, { key: 'email', step: 1 }, { key: 'phone', step: 1, required: false }, { key: 'website', step: 1, required: false }],
  formTitle: 'Book your strategy call',
  formDescription: 'Tell us where to reach you. You pick a time on the next screen.',
  formButton: 'Pick my call time',
  content: {
    sections: [
      { id: 'vc_hero', type: 'hero', text_size: 'md', space: 'loose', space_below: 'tight', logo: true,
        eyebrow: 'For coaches and consultants',
        headline: 'We will help you install an organic content system that brings in clients every week', highlight: 'brings in clients every week',
        subheadline: 'So you can sign clients consistently without paying for ads or posting every day.',
        cta_label: 'I am ready to grow', cta_href: '#book' },
      { id: 'vc_video', type: 'video', heading: 'Step 1: Watch the video below', poster_url: img('poster-vsl'), space: 'tight' },
      { id: 'vc_book', type: 'form', after_submit: 'thank_you', anchor: 'book', layout: 'split', form_side: 'right', bg: 'tint', inset: true,
        headline: 'Step 2: Book a call with our team', subheadline: 'A 45-minute strategy call. We look at what you are doing now and map the fastest path to steady clients.',
        items: ['A personal breakdown of what is holding you back', 'A walkthrough of the system our clients use', 'A clear roadmap, whether or not we work together'] },
      { id: 'vc_get', type: 'features', variant: 'media', heading: 'What you get on this call', highlight: 'on this call',
        features: [
          { image: img('scene-team'), title: 'Personalized breakdown', body: 'Of your current content and offer, and what to fix first.' },
          { image: img('dark-dashboard'), title: 'The full system walkthrough', body: 'How our clients plan, publish and convert without ads.' },
          { image: img('scene-design-3'), title: 'Your 90-day roadmap', body: 'Exactly what to do next, written down for you.' },
        ] },
      { id: 'vc_cases', type: 'testimonials', bg: 'tint', heading: 'Real results from real clients',
        quotes: [
          { image: img('case-1'), quote: 'We went from referrals only to 14 booked calls a month in one quarter, all from content.', name: 'Grace Lin', role: 'Cafe owner', photo_url: AVATAR(1) },
          { image: img('case-2'), quote: 'My content finally leads somewhere. I signed four clients from one video series.', name: 'Marcus Bell', role: 'Business coach', photo_url: AVATAR(2) },
          { image: img('case-3'), quote: 'The roadmap from the first call was worth it on its own. We followed it step by step.', name: 'Alicia Moore', role: 'Brand consultant', photo_url: AVATAR(3) },
          { image: img('case-4'), quote: 'Steady inbound for the first time in 30 years of business.', name: 'Frank Dawson', role: 'Workshop owner', photo_url: AVATAR(4) },
        ] },
      { id: 'vc_how', type: 'steps', variant: 'cards', heading: 'How it works',
        steps: [
          { label: 'Watch the video', detail: 'See exactly how the system works and who it is for.' },
          { label: 'Book your call', detail: 'Pick a time that suits you. It takes 30 seconds.' },
          { label: 'Get your roadmap', detail: 'Leave the call with a clear plan, whether or not we work together.' },
        ] },
      { id: 'vc_faq', type: 'faq', heading: 'Questions',
        faqs: [
          { q: 'Is the call a sales pitch?', a: 'No. You get a real breakdown and a roadmap. If it makes sense to work together, we will tell you how.' },
          { q: 'Who is this for?', a: 'Coaches, consultants and service businesses who already have happy clients and want more of them.' },
          { q: 'How long is the call?', a: 'About 45 minutes, on Zoom.' },
        ] },
      { id: 'vc_cta', type: 'cta', bg: 'brand', heading: 'Ready to stop guessing?', subheadline: 'Book your call and leave with a plan.', label: 'Book my call', href: '#book' },
    ],
    thank_you: {
      headline: 'Almost done. Pick your time.', subhead: 'Choose a time below and we will see you on the call.',
      sections: [
        { id: 'vc_ty_h', type: 'hero', headline: 'Almost done. Pick your time.', subheadline: 'Choose a slot below. You will get a confirmation email and a reminder before the call.' },
        { id: 'vc_ty_b', type: 'booking', booking_slug: '' },
      ],
    },
    footer: footer('vc_ft', 'Growth Studio'),
  },
};
